import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { connect, useSelector } from 'react-redux';
import { PhoneInput, InformModal, ValidationMessagesModal } from '../../../common/SharedComponents';
import { AppraisalsApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import LoadPanel from 'devextreme-react/load-panel';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';

const ReviewerModal = () => {
    const { isOpen, reviewerId, onSave } = useSelector((state) => state.application.editReviewerModal);
    const { dispatch } = store;

    const [firstName, set_firstName] = useState('');
    const [lastName, set_lastName] = useState('');
    const [email, set_email] = useState('');
    const [phone, set_phone] = useState('');
    const [isActive, set_isActive] = useState(true);

    const [validation, set_validation] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [openNamesNotUnique, set_openNamesNotUnique] = useState(false);

    const api = new AppraisalsApi.AppraisalsApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: { marginRight: '20px', textAlign: 'right' },
        form: { paddingLeft: '5px', paddingTop: '10px' },
        formRow: { marginBottom: '0' },
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setEditReviewerModal({
                editReviewerModal: { isOpen: false },
            })
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        set_loadingMessage('Validating...');
        set_loadingOpen(true);

        //Ensure names are unique...
        const model = {
            id: reviewerId,
            firstName,
            lastName,
            email,
            phone,
            isDeleted: !isActive,
        };

        const response = await api.validateAppraisalReviewer(model);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }

        if (response.result.hasErrors || response.result.hasWarnings) {
            set_validation(response.result);
            set_loadingOpen(false);
            return false;
        }

        await saveReviewer();
    };

    const saveReviewer = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: reviewerId,
                firstName,
                lastName,
                email,
                phone,
                isDeleted: !isActive,
            };

            set_loadingMessage('Saving changes...');
            const response = await api.appraisalReviewerSet(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            refreshEvents.dispatchRefreshEvent();

            const savedReviewer = response.result;
            onSave && onSave(savedReviewer.id);

            handleAdminSuccess('Reviewer Saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function isOpenChanged() {
            if (!isOpen) {
                return;
            }

            let reviewer = null;
            if (reviewerId && reviewerId > 0) {
                const response = await api.appraisalReviewerGet(reviewerId);
                if (response.statusCode !== 200) {
                    return;
                }
                reviewer = response.result;
            } else {
                reviewer = {
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    isDeleted: false,
                };
            }

            set_firstName(reviewer.firstName ?? '');
            set_lastName(reviewer.lastName ?? '');
            set_email(reviewer.email ?? '');
            set_phone(reviewer.phone ?? '');
            set_isActive(!reviewer.isDeleted);

            set_loadingOpen(false);
        }

        isOpenChanged();
    }, [isOpen, reviewerId]);

    const onValidationConfirm = async () => {
        set_validation(null);
        await saveReviewer();
    };

    const onValidationCancel = () => {
        set_validation(null);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            maxWidth='sm'
            open={isOpen}
            onClose={_handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '800px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <form
                                name='reviewerForm'
                                onSubmit={(event) => onSubmit(event)}
                            >
                                <div className='scrollable-content'>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>Reviewer Detail</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <div
                                        style={styles.form}
                                        className={'standard-form'}
                                    >
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='firstName'>
                                                    First Name
                                                    <span className='req'>*</span>
                                                </label>
                                                <input
                                                    id='firstName'
                                                    type='text'
                                                    name='firstName'
                                                    className='req'
                                                    required
                                                    value={firstName}
                                                    onChange={(e) => set_firstName(e.target.value)}
                                                />
                                            </div>
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='lastName'>
                                                    Last Name
                                                    <span className='req'>*</span>
                                                </label>
                                                <input
                                                    id='lastName'
                                                    type='text'
                                                    name='lastName'
                                                    className='req'
                                                    required
                                                    value={lastName}
                                                    onChange={(e) => set_lastName(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div
                                                className='columns small-12 large-9'
                                                style={{
                                                    paddingBottom: '5px',
                                                    paddingTop: '10px',
                                                }}
                                            >
                                                <input
                                                    type='checkbox'
                                                    id='isActive'
                                                    checked={isActive}
                                                    onChange={(event) => set_isActive(event.target.checked)}
                                                />{' '}
                                                Active
                                            </div>
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <label htmlFor='email'>
                                                    Email
                                                    <span className='req'>*</span>
                                                </label>
                                                <input
                                                    id='email'
                                                    type='email'
                                                    name='email'
                                                    className='req'
                                                    required
                                                    value={email}
                                                    onChange={(e) => set_email(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-4'>
                                                <label htmlFor='phone'>
                                                    Phone
                                                    <span className='req'>*</span>
                                                </label>
                                                <PhoneInput
                                                    id='phone'
                                                    value={phone}
                                                    onChange={(value) => set_phone(value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                    >
                                        Submit
                                    </button>
                                    <button
                                        type='button'
                                        className='button hollow'
                                        data-dismiss='modal'
                                        onClick={_handleClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                            <InformModal
                                isOpen={openNamesNotUnique}
                                title='Duplicate Appraiser!'
                                text='The Company, First and Last names that you have entered already exist in the database.'
                                confirmCallback={() => set_openNamesNotUnique(false)}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ValidationMessagesModal
                open={!!validation}
                validations={validation}
                handleConfirm={onValidationConfirm}
                handleCancel={onValidationCancel}
            />
        </Dialog>
    );
};

export default ReviewerModal;
